package mapsearch.zones

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SiteRestriction
import apiclient.geoobjects.isFlagged
import apiclient.geoobjects.pointGeometry
import apiclient.tags.floorId
import com.jillesvangurp.geojson.Geometry
import data.objects.ActiveObjectStore
import data.users.settings.LocalSettingsStore
import dev.fritz2.core.RootStore
import koin.withKoin
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.combine
import map.MapStateStore
import maplibreGL.FormationMap
import maplibreGL.LngLat
import maplibreGL.MaplibreGeoJSON
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.Marker
import maplibreGL.generateMapMarker
import maplibreGL.renderer.ADDITIONAL_GEOMETRY
import maplibreGL.toJs
import mapsearch.ClusterService
import mapsearch.MapRenderOverrides
import mapsearch.MarkerGenerators.makeSvgMarkerForMap
import mapsearch.buildings.ActiveFloorsStore
import mapsearch.markerEventListener
import mapsearch.tiles
import mu.KotlinLogging
import search.getSvgIconOptions
import svgmarker.MarkerSize

private val log = KotlinLogging.logger("ZoneSearchSpecStore")

data class ZoneSearchSpec(
    val includedZones: Set<String>? = null,
    val siteRestriction: SiteRestriction? = null
)

class ZoneSearchSpecStore : RootStore<ZoneSearchSpec>(ZoneSearchSpec(), Job()) {

    fun initialize() {
        startZonesSearch()
        processOverrideChange()
        processFloorChanges()
    }

    val includeZones = handle<Set<String>?> { current, includedIds ->
        current.copy(includedZones = includedIds)
    }

    private var activeRenderedZones: Map<String, GeoObjectDetails> = emptyMap()
    private var activeZone: GeoObjectDetails? = null

    fun removeZonesFromMap(zoneIds: List<String>) {
        // remove markers from map
        zoneIds.forEach { id ->
            currentRenderedZonesMarkers[id]?.remove()
        }
    }

    private fun processOverrideChange() {
        withKoin {
            val mapRenderOverrides: MapRenderOverrides = get()

            mapRenderOverrides.pathToolActive.data handledBy { _ ->
                reRenderAll()
            }
        }
    }

    private fun processFloorChanges() {
        withKoin {
            val activeFloorsStore = get<ActiveFloorsStore>()

            activeFloorsStore.data handledBy { floorIds ->
                try {
//                    log.info { "New SiteRestriction ${floorIds.toString()}" }
                    current.let { oldSpec ->
                        val newSiteRestriction = if (!floorIds.isNullOrEmpty()) {
                            oldSpec.siteRestriction?.copy(floorIds = floorIds)
                                ?: SiteRestriction(floorIds = floorIds)
                        } else null
                        update(oldSpec.copy(siteRestriction = newSiteRestriction))
                    }
                } catch (e: Exception) {
                    log.error(e) { "error processing site restriction for search spec" }
                }
            }
        }
    }

    fun addOrUpdateExistingZone(zone: GeoObjectDetails) {
        withKoin {
            val zoneObjectLookUpStore: ZoneObjectLookUpStore = get()
            if (zone.id == activeZone?.id) {
                activeZone = zone
            }
            zoneObjectLookUpStore.upsertZone(zone)
        }.also {
            reRenderSingleZone(zone)
        }
    }

    private fun startZonesSearch() {
        withKoin {
            val mapStateStore: MapStateStore = get()
            val zoneObjectLookUp: ZoneObjectLookUpStore = get()
            val zoneTilesLookUp: ZoneTilesLookUpStore = get()
            val clusterService: ClusterService = get()

            combine(
                zoneTilesLookUp.data,
                data,
                mapStateStore.data,
            ) { tiles, spec, map -> Triple(tiles, spec, map) } handledBy { (tilesLookup, zoneSpec, mapState) ->
                try {
                    if (mapState != null) {
                        delay(200) // let clustering a bit time before it gets triggered again
                        val ids = mapState.tiles().let { (zoom, tiles) ->
                            tiles.flatMap { tile ->
                                tilesLookup[tile].orEmpty()
                            }
                        }
                        // update currentBuildingsStore
                        val uniqueZones = ids.mapNotNull { id ->
                            // Check "zoneSpec.includedZones":
                            // not null => GlobalSearch is active -> only show zones included in search results, if empty show none
                            // null => MapSearch -> show all zones
                            if (zoneSpec.includedZones == null || id in zoneSpec.includedZones) {
                                zoneObjectLookUp.current[id]?.let { zone ->
                                    // Apply Site Restrictions
                                    val activeFloorIds = zoneSpec.siteRestriction?.floorIds
                                    val showZone = if (!activeFloorIds.isNullOrEmpty()) {
                                        (zone.tags.floorId?.let { floorId ->
                                            floorId in activeFloorIds
                                        } ?: true)
                                    } else true

                                    if (showZone) {
                                        id to zone
                                    } else {
//                                        console.log("DON'T SHOW Zone: ${zone.title}")
                                        null
                                    }
                                }
                            } else null
                        }.toMap()
                        activeRenderedZones = uniqueZones
                        // send zones to ClusterService
                        clusterService.setFeaturesFromZoneSearch(uniqueZones)
//                        renderZones(uniqueZones)
                    }
                } catch (e: Exception) {
                    log.error(e) { "Error handling new searchSpec" }
                }
            }
        }
    }

    private val currentRenderedZonesMarkers = mutableMapOf<String, Marker>()
    private val currentRenderedGeoJSONZones = mutableMapOf<String, List<MaplibreGeoJSON>>()

    private fun reRenderAll() {
        removeZonesFromMap(activeRenderedZones.keys)
        renderZones(activeRenderedZones)
    }

    fun reRenderSingleZone(zone: GeoObjectDetails) {
//        log.info { "Re-Render single zone: ${zone.title}" }
        withKoin {
            val formationMap: FormationMap = get()
            val mapRenderOverrides: MapRenderOverrides = get()
            val map = formationMap.map
            // Re-render Marker
            if (map != null) {
                currentRenderedZonesMarkers.remove(zone.id)?.remove()
                createZoneMarker(zone)?.let { newMarker ->
                    currentRenderedZonesMarkers[zone.id] = newMarker
                    if (zone.id !in mapRenderOverrides.hiddenMarkers) {
                        newMarker.addTo(map)
                    }
                }

                // Re-render geometry
                removeZoneGeoJSONGeometryFromMap(zone.id)
                createZoneGeoJSONGeometry(zone)?.let { geoJSONs ->
                    currentRenderedGeoJSONZones[zone.id] = geoJSONs
                    formationMap.geoJSONAdd(
                        features = geoJSONs.map { it.feature },
                        sourceId = ADDITIONAL_GEOMETRY,
                        replaceExisting = true,
                    )
                }
            }
        }
    }

    private fun createZoneMarker(zone: GeoObjectDetails): Marker? {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            val localSettingsStore: LocalSettingsStore = get()
            val mapRenderOverrides: MapRenderOverrides = get()

            return getSvgIconOptions(
                obj = zone,
                flagged = zone.isFlagged,
                desaturated = mapRenderOverrides.pathToolActive.current,
                showStateIndicator = true,
            )?.let { svgIconOptions ->
                val markerOptions = MaplibreMarkerOptions(
                    element = makeSvgMarkerForMap(
                        id = zone.id,
                        title = zone.title,
                        svgIconOptions = svgIconOptions.copy(size = MarkerSize.S),
                        objectType = ObjectType.Zone,
                        active = activeObjectStore.current.id == zone.id,
                        showTitle = !localSettingsStore.current.hideMarkerTitles,
                    ).also { element ->
                        if (!svgIconOptions.desaturated) { // make non-clickable when desaturated (= disabled)
                            element.addEventListener("click", markerEventListener)
                        }
                    },
                )
                val newMarker = Marker(markerOptions.toJs())
                newMarker.setLngLat(LngLat(zone.latLon.lon, zone.latLon.lat))
                newMarker
            }
        }
    }

    private fun createZoneGeoJSONGeometry(zone: GeoObjectDetails): List<MaplibreGeoJSON>? {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            return generateMapMarker(
                geoObject = zone,
                activeObjectId = activeObjectStore.current.id,
            )?.geoJSONs
        }
    }

    fun updateZoneGeometry(
        geoObject: GeoObjectDetails,
        newGeometry: Geometry = geoObject.geometry ?: geoObject.latLon.pointGeometry(),
    ) {
//        log.info { "Update zone geometry for: ${geoObject.title}" }
        withKoin {
            val formationMap: FormationMap = get()
            val zoneTilesLookUpStore: ZoneTilesLookUpStore = get()
            val zoneObjectLookUpStore: ZoneObjectLookUpStore = get()
            formationMap.geoJsonUpdateData(
                formationMap.createGeoJSONSourceDiff(
                    update = listOf(
                        formationMap.createGeoJSONFeatureDiff(
                            id = "${geoObject.id}-geometry",
                            newGeometry = newGeometry,
                        ),
                    ),
                ),
                ADDITIONAL_GEOMETRY,
            )
            zoneObjectLookUpStore.upsertZone(geoObject.copy(geometry = newGeometry))
            zoneTilesLookUpStore.updateZoneInTileLookUp(geoObject.id, geoObject.latLon)
        }
    }

    /**
     * Removes the zone geometry from the map
     */
    private fun removeZoneGeoJSONGeometryFromMap(zoneId: String) {
        withKoin {
            val formationMap: FormationMap = get()
            currentRenderedGeoJSONZones.remove(zoneId)?.let { zoneGeometries ->
                formationMap.geoJSONRemove(
                    zoneGeometries.map { it.id }.toTypedArray(),
                    ADDITIONAL_GEOMETRY,
                )
            }
        }
    }

    /**
     * Removes a zone completely from the map, marker and geometry
     */
    fun removeZoneFromMap(zoneId: String) {
        currentRenderedZonesMarkers.remove(zoneId)?.remove()
        removeZoneGeoJSONGeometryFromMap(zoneId)
    }

    fun removeZonesFromMap(zoneIds: Set<String>) {
        zoneIds.forEach { zoneId ->
            removeZoneFromMap(zoneId)
        }
    }

    fun renderZones(zones: Map<String, GeoObjectDetails>) {
//        log.info { "Render zones: ${zones.map { it.value.title }}" }
        withKoin {
            val formationMap: FormationMap = get()
            val mapRenderOverrides: MapRenderOverrides = get()
            val map = formationMap.map

            if (map != null) {
                removeZonesFromMap(currentRenderedZonesMarkers.keys - zones.keys)

                val newZones = zones.filter { (id, _) -> id !in currentRenderedZonesMarkers.keys }
                // Render marker and geometry of zones that are not already rendered
                newZones.forEach { (zoneId, zone) ->
                    // Marker
                    createZoneMarker(zone)?.let { newMarker ->
                        currentRenderedZonesMarkers[zoneId] = newMarker
                        if (zoneId !in mapRenderOverrides.hiddenMarkers) {
                            newMarker.addTo(map)
                        }
                    }
                    // GeoJSON geometry
                    createZoneGeoJSONGeometry(zone)?.let { geoJSONs ->
                        currentRenderedGeoJSONZones[zone.id] = geoJSONs
                    }
                }.also {
                    formationMap.geoJSONAdd(
                        (currentRenderedGeoJSONZones.flatMap { it.value }).map { it.feature },
                        ADDITIONAL_GEOMETRY,
                    )
                }
            }
        }
    }

//    init {
//        data handledBy { zoneSpec ->
//            console.log("ZoneSpec log:", zoneSpec.toString())
//        }
//    }
}
